import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { ILead, LeadRoutingOutcome, isDuplicateRouting } from "../../models/Lead";

/**
 * Shown at the top of a lead that was created for a vehicle another booking already held.
 * Renders nothing on an ordinary lead, so it is safe to mount unconditionally.
 *
 * The two outcomes mean different things and the wording has to say which:
 * - DivertedDuplicateVrm: a marketplace quote, so SAV took ownership to arbitrate rather than
 *   letting the price winner open a second booking or handing the first buyer another company's
 *   price.
 * - FlaggedDuplicateVrm: the quote came from the customer's own website, so it stayed with them.
 */
export default function DuplicateVehicleBanner({ lead }: { lead: ILead }) {
  if (!isDuplicateRouting(lead.routingOutcome)) return null;

  const diverted = lead.routingOutcome === LeadRoutingOutcome.DivertedDuplicateVrm;

  const heading = diverted
    ? "Second booking for this vehicle — sent to SAV"
    : "Second booking for this vehicle";

  return (
    <div
      role="status"
      className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4"
    >
      <div className="flex gap-3">
        <ExclamationTriangleIcon
          className="h-5 w-5 flex-shrink-0 text-amber-600"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-amber-900">{heading}</h3>

          {lead.routingReason && (
            <p className="mt-1 text-sm text-amber-800">{lead.routingReason}</p>
          )}

          <p className="mt-2 text-sm text-amber-800">
            Check the other booking before working this one — the vehicle can only be
            collected once.
          </p>

          {lead.duplicateOfLeadId && (
            <Link
              to={`/leads/${lead.duplicateOfLeadId}`}
              className="mt-2 inline-flex items-center text-sm font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950"
            >
              Open the booking that already holds this vehicle
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
